import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { Link } from "gatsby"

import { useUser } from "store/contexts/user"
import { useLocation } from "store/contexts/location"

import Icon from "icons"
import AuthenticateModal from "components/article/share/browser-share/authenticate-modal"
import {
  MenuButton,
  Menu,
  AccountText,
  ViewMoreInfoLink,
  AccountMenuContainer,
  ItemContainer,
} from "./styles"

const AccountMenu = ({ variant }) => {
  const menuButton = useRef(null)
  const menu = useRef(null)

  const [isOpened, setOpened] = useState(false)
  const [hoverContent, setHoverContent] = useState("")
  const [authenticateModalOpen, setAuthenticateModalOpen] = useState(false)

  const {
    user: { loggedIn },
  } = useUser()
  const { location } = useLocation()

  const accountHeading = useMemo(
    () => ({
      key: "link-0",
      link: loggedIn ? "#" : `/account/login`,
      label: "Account",
      content: loggedIn ? "Modify your account settings" : "Login to your account",
    }),
    [loggedIn],
  )

  const accountItems = useMemo(
    () => [
      {
        key: "link-1",
        link: "https://account.mindbodygreen.com",
        label: "Orders & Tracking",
        bypassLogin: true,
        content: "Track orders and see past purchases",
      },
      {
        key: "link-2",
        link: "http://shop.mindbodygreen.com/customer_authentication/login?return_to=/a/account/shopify-login",
        label: "Subscriptions",
        bypassLogin: true,
        content: "Manage your current subscriptions",
      },
      {
        key: "link-3",
        link: `${process.env.GATSBY_CLASSES_APP_DOMAIN}/user/classes`,
        externalLink: true,
        label: "Classes",
        content: "Watch classes you have enrolled in",
      },
      {
        key: "link-4",
        link: `/user/saved-items`,
        label: "Saved Articles",
        type: "savedArticles",
        content: "View articles that you have saved for later",
      },
      {
        key: "link-5",
        link: "https://mindbodygreen.zendesk.com/hc/en-us",
        externalLink: true,
        bypassLogin: true,
        label: "Support",
        content: "Contact us for help",
      },
      ...(loggedIn
        ? [
            {
              key: "link-6",
              link: `https://${process.env.GATSBY_AUTH0_API_DOMAIN}/v2/logout?client_id=${process.env.GATSBY_AUTH0_APP_CLIENT_ID}&returnTo=${location.origin}/account/callback/logout`,
              externalLink: true,
              label: "LOG OUT",
              content: "Log out of your account",
            },
          ]
        : []),
    ],
    [loggedIn, location],
  )

  const closeMenu = useCallback(
    (e) => {
      if (
        menu.current &&
        isOpened &&
        !menu.current.contains(e.target) &&
        !menuButton.current.contains(e.target)
      ) {
        setOpened(false)
      }
    },
    [isOpened],
  )

  useEffect(() => {
    document.addEventListener("mousedown", closeMenu)
    return () => {
      document.addEventListener("mousedown", closeMenu)
    }
  }, [closeMenu])

  const renderLink = useCallback(
    (item) => {
      let href = item.link
      if (!loggedIn && !item.bypassLogin) {
        href = "/account/login"
        if (item.label !== "Login") {
          href += `?redirectTo=${item.link}`
        }
      }

      const props = {
        href,
        rel: "nofollow",
        role: "menuitem",
        onClick: () => setOpened(false),
      }

      return item.externalLink && !href.startsWith("/account/login") ? (
        <a {...props}>{item.label}</a>
      ) : (
        <Link {...props} to={props.href}>
          {item.label}
        </Link>
      )
    },
    [loggedIn],
  )

  const toggleMenu = () => {
    setOpened((prev) => !prev)
  }

  return (
    <AccountMenuContainer>
      <MenuButton
        type="button"
        aria-controls="account-menu"
        aria-expanded={isOpened}
        aria-label="Open account menu"
        onClick={toggleMenu}
        onKeyDown={(e) => e.key === "Escape" && setOpened(false)}
        ref={menuButton}
      >
        <Icon
          id="account-icon"
          height={23}
          width={23}
          aria-hidden="true"
          title={<React.Suspense>{accountHeading.label}</React.Suspense>}
        />
      </MenuButton>

      {isOpened && (
        <Menu
          pageVariant={variant}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setOpened(false)
            }
          }}
          ref={menu}
          onKeyDown={(e) => e.key === "Escape" && setOpened(false)}
          role="menu"
          aria-label="Open account menu"
        >
          {accountItems.map((item) => (
            <React.Suspense key={item.key}>
              <ItemContainer
                onMouseOver={() => setHoverContent(item.content)}
                onFocus={() => setHoverContent(item.content)}
                aria-label={item.content || item.label}
              >
                <li>
                  {renderLink(item)}
                  <p>{item.content}</p>
                </li>
              </ItemContainer>
            </React.Suspense>
          ))}
          <ViewMoreInfoLink tabIndex="-1">{hoverContent}</ViewMoreInfoLink>
        </Menu>
      )}

      {authenticateModalOpen && (
        <AuthenticateModal
          customRedirectTo={location.pathname}
          onClose={() => setAuthenticateModalOpen(false)}
          onSuccess={() => setAuthenticateModalOpen(false)}
        />
      )}
    </AccountMenuContainer>
  )
}

export default AccountMenu
